import { z } from 'zod';
import { slugSchema } from './catalog.js';

export const cartGeometrySchema = z.object({
  widthMm: z.number().positive().max(100_000),
  heightMm: z.number().positive().max(100_000),
  cutLengthMm: z.number().positive().max(10_000_000).optional(),
});

export const addCartItemSchema = z.object({
  productSlug: slugSchema,
  selections: z.record(z.string(), z.string()).default({}),
  qty: z.number().int().min(1).max(1_000_000),
  geometry: cartGeometrySchema.optional(),
});

export const updateCartItemSchema = z
  .object({
    selections: z.record(z.string(), z.string()).optional(),
    qty: z.number().int().min(1).max(1_000_000).optional(),
    geometry: cartGeometrySchema.optional(),
  })
  .refine(
    (data) =>
      data.selections !== undefined || data.qty !== undefined || data.geometry !== undefined,
    { message: 'Tidak ada perubahan yang dikirim' },
  );

export const cartItemParamsSchema = z.object({
  itemId: z.string().min(1).max(64),
});

/** Batas halaman desain per item (KEPUTUSAN 4.6). */
export const MAX_DESIGN_PAGES = 12;

/** Halaman desain yang dituju endpoint desain item; bawaan halaman 1. */
export const designPageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(MAX_DESIGN_PAGES).default(1),
});

export type DesignPageQuery = z.infer<typeof designPageQuerySchema>;

/**
 * Jumlah sisi desain item dari pilihannya: nilai `sides` terbesar di antara
 * nilai yang dipilih, bawaan 1 (KEPUTUSAN 4.6).
 */
export function sidesForSelections(
  options: { key: string; values: { value: string; sides: number | null }[] }[],
  selections: Record<string, string>,
): number {
  let sides = 1;
  for (const option of options) {
    const chosen = option.values.find((v) => v.value === selections[option.key]);
    if (chosen?.sides && chosen.sides > sides) sides = chosen.sides;
  }
  return Math.min(sides, MAX_DESIGN_PAGES);
}

/** Label opsi yang ikut disimpan sebagai snapshot pada item keranjang. */
export const optionLabelSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.string(),
  valueLabel: z.string(),
});

export type CartGeometry = z.infer<typeof cartGeometrySchema>;
export type AddCartItemInput = z.infer<typeof addCartItemSchema>;
export type UpdateCartItemInput = z.infer<typeof updateCartItemSchema>;
export type CartItemParams = z.infer<typeof cartItemParamsSchema>;
export type OptionLabel = z.infer<typeof optionLabelSchema>;
